import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getPropertyAccess, Location as ApiLocation } from '@/API/apiService';
import MapDisplay from './MapDisplay';

// ----------------------------------
// ExtendedRoute, ExtendedLocation の型定義
// ----------------------------------
interface ExtendedRoute {
  route_type: 'A' | 'B' | 'C';
  route_sort_number: string;
  station_title?: string;
  station_title_en?: string;
  walk_title?: string;
  walk_title_en?: string;
  line_type?: number; // 0 => dotted, 1 => solid
}

interface ExtendedLocation extends ApiLocation {
  name?: string;
  name_en?: string;
  address?: string;
  address_en?: string;
  postal_code?: string;
  postal_code_en?: string;
  routes?: ExtendedRoute[];
}

type LocationFetcherProps = {
  propertyId?: string;
};

// ----------------------------------
// アイコンコンポーネント等（省略）
// ----------------------------------

// 途中目的地(■)
const IconSquare = () => (
  <span className="inline-block text-black mr-2">■</span>
);

// 最終目的地(ピン) - SVGアイコン
const IconPin = () => (
  <svg
    xmlns="http://maps.google.com/mapfiles/ms/icons/red-dot.png"
    viewBox="0 0 24 24"
    fill="black"
    className="inline-block w-5 h-5 mr-0 -ml-[3px]"
  >
    <path d="M12 0C8.134 0 5 3.134 5 7c0 3.949 3.873 9.737 6.014 12.838a1 1 0 0 0 1.572 0C15.127 16.737 19 10.949 19 7c0-3.866-3.134-7-7-7z" />
  </svg>
);

// RouteLine: 線の表示 (点線 or 実線)
const RouteLine: React.FC<{ lineType?: number; walkTitle?: string }> = ({
  lineType,
  walkTitle,
}) => {
  const borderStyle = lineType === 0 ? 'border-dashed' : 'border-solid';
  return (
    <div className="flex items-center text-black">
      <div className={`border-l-2 border-black h-6 ml-2 mr-4 ${borderStyle}`} />
      <span className="text-sm">{walkTitle}</span>
    </div>
  );
};

// SingleRouteDisplay: 1つのルート (A/B/C) を表示
const SingleRouteDisplay: React.FC<{
  routeLabel: string;
  steps: ExtendedRoute[];
  propertyName: string;
  useEnglish: boolean;
}> = ({ routeLabel, steps, propertyName, useEnglish }) => {
  const sortedSteps = [...steps].sort(
    (a, b) =>
      parseInt(a.route_sort_number, 10) - parseInt(b.route_sort_number, 10)
  );

  return (
    <div className="flex mb-6">
      {/* 左側: ルートラベル */}
      <div className="w-24 flex-shrink-0 mr-4">{routeLabel}:</div>
      {/* 右側: ルート詳細 */}
      <div>
        {sortedSteps.map((step, idx) => {
          const isLast = idx === sortedSteps.length - 1;
          const stationTitle = useEnglish
            ? step.station_title_en
            : step.station_title;
          const walkTitle = useEnglish ? step.walk_title_en : step.walk_title;

          return (
            <div key={idx}>
              <div className="flex items-center mb-1 text-black">
                <IconSquare />
                <span className="text-sm">{stationTitle}</span>
              </div>
              {!isLast ? (
                <RouteLine lineType={step.line_type} walkTitle={walkTitle} />
              ) : (
                <div className="flex items-center">
                  <RouteLine lineType={step.line_type} walkTitle={walkTitle} />
                </div>
              )}
            </div>
          );
        })}
        {/* 最終目的地 (物件名) */}
        <div className="flex items-center mt-1 text-black">
          <IconPin />
          <span className="text-sm">{propertyName}</span>
        </div>
      </div>
    </div>
  );
};

// ------------------ メインコンポーネント ------------------
export const LocationFetcher: React.FC<LocationFetcherProps> = ({
  propertyId,
}) => {
  // URL から言語判定: URL の先頭セグメントが 'en' なら英語、それ以外なら日本語
  const urlLocation = useLocation();
  const langSegment = urlLocation.pathname.split('/')[1];
  const useEnglish = langSegment === 'en';

  const [locationData, setLocationData] = useState<ExtendedLocation | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    setLoading(true);
    getPropertyAccess(propertyId)
      .then((res) => {
        setLocationData(res.data.data as ExtendedLocation);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setError('Failed to fetch location data');
        setLoading(false);
      });
  }, [propertyId]);

  if (loading) return <div>Loading map...</div>;
  if (error) return <div>{error}</div>;
  if (!locationData) return <div>No location data available.</div>;

  // 物件名 (言語切り替え)
  const propertyName = useEnglish
    ? locationData.name_en || locationData.name || 'No Property Name'
    : locationData.name || 'No Property Name';

  // 住所 (言語切り替え)
  const addressJP = `${locationData.address ?? ''} ${locationData.postal_code ?? ''}`;
  const addressEN = `${locationData.postal_code_en ?? ''} ${locationData.address_en ?? ''}`;
  const displayAddress = useEnglish ? addressEN : addressJP;

  // ルート
  const routes = locationData.routes ?? [];
  const routeLabels: Record<'A' | 'B' | 'C', string> = {
    A: useEnglish ? 'Route A' : 'ルート A',
    B: useEnglish ? 'Route B' : 'ルート B',
    C: useEnglish ? 'Route C' : 'ルート C',
  };
  const routeTypes: Array<'A' | 'B' | 'C'> = ['A', 'B', 'C'];
  const getStepsForType = (t: 'A' | 'B' | 'C') =>
    routes.filter((r) => r.route_type === t);

  return (
    <div className="md:p-4">
      <div className="text-center mb-4 md:mb-14 text-xl">
        {useEnglish ? '(ACCESS)' : '(アクセス)'}
      </div>

      {/* レスポンシブレイアウト (Map 左 / コンテンツ右) */}
      <div className="flex flex-col md:flex-row gap-4">
        {/* 左カラム: Map */}
        <div className="md:w-1/2 w-full flex justify-center items-center relative z-[1]">
          <div className="w-full h-[300px] md:w-[587px] md:h-[609px]">
            <MapDisplay
              location={{
                latitude: locationData.latitude,
                longitude: locationData.longitude,
              }}
            />
          </div>
        </div>

        {/* 右カラム: 住所 & ルート */}
        <div className="md:w-1/2 w-full md:py-10 px-4 md:px-24">
          <div className="flex mb-6 text-black">
            <div className="w-24 flex-shrink-0 mr-4">
              {useEnglish ? 'Address:' : '住所:'}
            </div>
            <div>{displayAddress}</div>
          </div>

          {routeTypes.map((type) => {
            const steps = getStepsForType(type);
            if (steps.length === 0) return null;
            return (
              <SingleRouteDisplay
                key={type}
                routeLabel={routeLabels[type]}
                steps={steps}
                propertyName={propertyName}
                useEnglish={useEnglish}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default LocationFetcher;
